/**
 * Creates pseudocode representation of a complete Scratch project.
 * @param {*} project Scratch runtime project object
 * @returns {string} string representing pseudocode of the project
 */
export function preprocessProject(project) {

    /**
     * Converts scratch's representation of literals into a pseudocode
     * @returns {string} pseudocode
    */
    function toLiteral(arr) {
        // scratch represents literals as a specifically created array
        if (!arr) return "null"
        if (arr[0] >= 4 && arr[0] <= 10) // normal literal (number, string, ..)
            return arr[1].toString()
        if (arr[0] == 11) // broadcast
            return "BROADCAST_" + arr[1]
        if (arr[0] == 12) // variable
            return "VAR_" + arr[1]
        if (arr[0] == 13) // list variable
            return "LIST_" + arr[1]
        
        throw Error('Preprocessor fail, cannot parse literals array', arr)
    }

    /** Repeats tab character X-times. */
    function tabs(cnt) {
        return "\t".repeat(cnt)
    }

    /** Recursively stringify a block. 
     * @param {*} block block to process
     * @param {*} target parent Sprite which owns the block
     * @param {boolean} doExpand continue processing blocks placed right next after this one
     * @param {number} tabCnt tab offset at which to put the pseudocode
     * @returns {string} pseudocode
    */
    function stringifyBlock(block, target, doExpand, tabCnt) {
        let str = tabs(tabCnt) + block.opcode + "("

        str += Object.keys(block.fields).map(inputKey => {
            const input = block.fields[inputKey][0]
            return `${inputKey.toLowerCase()}:"${input.replace('"', '\\"')}"`
        }).join(",")
        
        if (Object.keys(block.inputs).length > 0)
            str += ", "

        str += Object.keys(block.inputs).map(inputKey => {
            const input = block.inputs[inputKey][1]
            if (!Object.keys(target.blocks).includes(input))
                return `${inputKey.toLowerCase()}:"${toLiteral(input).replace('"', '\\"')}"` // literal
            else
                return `${inputKey.toLowerCase()}:{\n${stringifyBlock(target.blocks[input], target, true, tabCnt+1)}\n${tabs(tabCnt)}}`
        }).join(",")
        
        str += ")"

        if (doExpand && block.next !== null)
            str += '\n' + stringifyBlock(target.blocks[block.next], target, true, tabCnt)

        return str
    }


    // process the project

    let str = ""

    for (const target of project.targets) {
        str += `Begin Sprite '${target.name}'\n`

        if (!target.isStage) {
            str += `\tSize is ${target.size} %\n`
            str += `\tPosition is x=${target.x}, y=${target.y}\n`
            str += `\tRotation/direction is ${target.direction} degrees\n`
            str += `\tRotation style is '${target.rotationStyle}'\n`
            str += `\tSprite is currently ${target.visible ? "visible" : "invisible"}\n`
            str += `\tVolume is set to ${target.volume}\n`
        } else
            str += `\tThis is a Stage sprite, it is the background object in Scratch, it serves as a kind of global script.\n`
        if (Object.keys(target.variables).length > 0)
            str += `\tVariables: ${Object.values(target.variables).map(val => `${val[0]}='${val[1]}'`).join(", ")}\n`
    
        str += `\n`
        Object.keys(target.blocks).filter(id => target.blocks[id].parent === null).forEach(rootId => {
            let block = target.blocks[rootId]
            if (!block.opcode.startsWith("event") && block.opcode != "control_start_as_clone")
                str += "\t/* no event block, following chunk will probably not get run, unless its a custom method block */\n"
            str += stringifyBlock(block, target, false, 1) + " {\n"
            block = target.blocks[block.next]
            while (block) {
                str += stringifyBlock(block, target, false, 2) + "\n"
                block = target.blocks[block.next]
            }
            str += tabs(1) + "}\n\n"
        })

        str += `End Sprite '${target.name}'\n\n`
    }

    return str
}

// This file is used both in scratch frontend (commonJS) and in backend teacher editor (pure JS),
// so we check for commonJS environment via module object.
//if (module)
//    module.exports = preprocessProject;